import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DonkeyMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronLeft, Plus, Loader2, FlaskConical } from "lucide-react";

const STAGES = ["Unexamined", "Assumption identified", "Test designed", "Test running",
  "Evidence collected", "Validated", "Invalidated", "Inconclusive"];

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEvidence, setNewEvidence] = useState("");
  const [newInterview, setNewInterview] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/workspaces/${id}`); setWs(data); }
    catch { toast.error("Could not load workspace."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const patch = async (update, optimistic) => {
    setWs((w) => ({ ...w, ...optimistic }));
    await api.patch(`/workspaces/${id}`, update);
  };

  const setStage = (s) => patch({ validation_status: s }, { validation_status: s });
  const toggleTask = (tid) => {
    const tasks = ws.tasks.map((t) => t.id === tid ? { ...t, done: !t.done } : t);
    patch({ tasks }, { tasks });
  };
  const setAssumptionStage = (aid, stage) => {
    const assumptions = ws.assumptions.map((a) => a.id === aid ? { ...a, stage } : a);
    patch({ assumptions }, { assumptions });
  };
  const addEvidence = () => {
    if (!newEvidence.trim()) return;
    const evidence = [...(ws.evidence || []), { text: newEvidence.trim(), at: new Date().toISOString() }];
    patch({ evidence }, { evidence }); setNewEvidence("");
  };
  const addInterview = () => {
    if (!newInterview.trim()) return;
    const customer_interviews = [...(ws.customer_interviews || []), { text: newInterview.trim(), at: new Date().toISOString() }];
    patch({ customer_interviews }, { customer_interviews }); setNewInterview("");
  };
  const saveNotes = (notes) => patch({ notes }, { notes });

  if (loading || !ws) {
    return <div className="min-h-screen bg-donkey-bg flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-donkey-orange" /></div>;
  }

  const opp = ws.opportunity || {};
  const doneCount = (ws.tasks || []).filter((t) => t.done).length;

  return (
    <div className="min-h-screen bg-donkey-bg grain">
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-donkey-grey hover:text-donkey-warm text-sm" data-testid="back-dashboard">
            <ChevronLeft className="w-4 h-4" /> Dashboard
          </button>
          <button onClick={() => navigate(`/report/${ws.report_id}`)} className="text-sm text-donkey-orange hover:underline">View full report</button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        <div className="flex items-start gap-3 mb-8">
          <DonkeyMark size={36} className="mt-1" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange">Test your idea</p>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">{ws.opportunity_name}</h1>
            <p className="text-donkey-grey text-sm mt-1">{opp.proposition}</p>
          </div>
        </div>

        <div className="card-donkey rounded-2xl p-6 mb-6 border-l-2 border-donkey-orange" data-testid="validation-intro">
          <h2 className="font-heading font-semibold text-donkey-warm">Test your idea before spending serious time or money</h2>
          <p className="text-sm text-donkey-grey mt-2 leading-relaxed">
            This is where you check whether real people actually want this — with small, cheap tests before you commit.
            Work through the checklist below one step at a time. The Donkey has already turned your idea into the
            assumptions to check and the tasks to try first.
          </p>
        </div>

        {/* Validation status */}
        <div className="card-donkey rounded-2xl p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Where you're up to</p>
              <p className="text-donkey-warm">Record what really happened — don't just tick a box.</p>
            </div>
            <Select value={ws.validation_status} onValueChange={setStage}>
              <SelectTrigger className="w-56 bg-donkey-bg border-white/15 text-donkey-warm" data-testid="stage-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-donkey-deep border-white/15 text-donkey-warm">
                {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Assumptions */}
          <div className="card-donkey rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-donkey-warm mb-4">Assumption tracker</h3>
            <div className="space-y-3">
              {(ws.assumptions || []).map((a) => (
                <div key={a.id} className="rounded-xl bg-donkey-bg border border-white/10 p-3">
                  <p className="text-sm text-donkey-warm/90 mb-2">{a.text}</p>
                  <Select value={a.stage} onValueChange={(v) => setAssumptionStage(a.id, v)}>
                    <SelectTrigger className="h-8 text-xs bg-donkey-deep border-white/15 text-donkey-grey w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-donkey-deep border-white/15 text-donkey-warm">
                      {STAGES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {(!ws.assumptions || ws.assumptions.length === 0) && <p className="text-sm text-donkey-grey">No assumptions recorded.</p>}
            </div>
          </div>

          {/* Tasks */}
          <div className="card-donkey rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-donkey-warm">Task checklist</h3>
              <span className="font-mono text-sm text-donkey-grey">{doneCount}/{(ws.tasks || []).length}</span>
            </div>
            <div className="space-y-2.5">
              {(ws.tasks || []).map((t) => (
                <label key={t.id} className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={t.done} onCheckedChange={() => toggleTask(t.id)} className="mt-0.5 border-white/30" data-testid={`task-${t.id}`} />
                  <span className={`text-sm ${t.done ? "text-donkey-grey line-through" : "text-donkey-warm/90"}`}>{t.text}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 7-day test */}
          <div className="card-donkey rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-donkey-warm mb-4 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-donkey-orange" /> Seven-day test
            </h3>
            <ol className="space-y-2 list-decimal list-inside text-sm text-donkey-warm/85">
              {(opp.seven_day_test || []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>

          {/* Evidence log */}
          <div className="card-donkey rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-donkey-warm mb-4">Evidence log</h3>
            <div className="space-y-2 mb-3">
              {(ws.evidence || []).map((e, i) => (
                <div key={i} className="text-sm text-donkey-warm/85 rounded-lg bg-donkey-bg border border-white/10 p-3">{e.text}</div>
              ))}
              {(!ws.evidence || ws.evidence.length === 0) && <p className="text-sm text-donkey-grey">No evidence yet. Real proof beats optimism.</p>}
            </div>
            <div className="flex gap-2">
              <Input value={newEvidence} onChange={(e) => setNewEvidence(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEvidence()}
                placeholder="e.g. 3 of 5 prospects said they'd pay…" className="bg-donkey-bg border-white/10 text-sm h-9" data-testid="evidence-input" />
              <button onClick={addEvidence} className="text-donkey-orange shrink-0" aria-label="Add evidence"><Plus className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Customer interviews */}
          <div className="card-donkey rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-donkey-warm mb-4">Customer interview tracker</h3>
            <div className="space-y-2 mb-3">
              {(ws.customer_interviews || []).map((e, i) => (
                <div key={i} className="text-sm text-donkey-warm/85 rounded-lg bg-donkey-bg border border-white/10 p-3">{e.text}</div>
              ))}
              {(!ws.customer_interviews || ws.customer_interviews.length === 0) && <p className="text-sm text-donkey-grey">No conversations logged yet.</p>}
            </div>
            <div className="flex gap-2">
              <Input value={newInterview} onChange={(e) => setNewInterview(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addInterview()}
                placeholder="Who did you speak to and what did they say?" className="bg-donkey-bg border-white/10 text-sm h-9" data-testid="interview-input" />
              <button onClick={addInterview} className="text-donkey-orange shrink-0" aria-label="Add interview"><Plus className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Notes */}
          <div className="card-donkey rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-donkey-warm mb-4">Notes</h3>
            <Textarea defaultValue={ws.notes} onBlur={(e) => saveNotes(e.target.value)}
              placeholder="Anything else worth remembering…" className="min-h-[120px] bg-donkey-bg border-white/10 text-sm text-donkey-warm resize-none" data-testid="notes-input" />
          </div>
        </div>
      </div>
    </div>
  );
}
